import { useEffect, useRef, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { API } from "@/lib/api";
import { renderAIMessage, extractProductSlugs, ConciergeProductCard } from "@/lib/messageRenderer";

const SESSION_KEY = "rs_concierge_session";

const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `cs_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

// Brass ram-crest wax-seal — same sigil as the site cursor. Reused here as the
// send button so pressing it feels like sealing a letter.
const WAX_SEAL_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="22" height="22">
  <defs>
    <radialGradient id="cwax" cx="35%" cy="30%" r="70%">
      <stop offset="0%" stop-color="#c74c3d"/>
      <stop offset="55%" stop-color="#8a1c1c"/>
      <stop offset="100%" stop-color="#3a0808"/>
    </radialGradient>
    <linearGradient id="cbrass" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#f0d38a"/>
      <stop offset="55%" stop-color="#a67b2c"/>
      <stop offset="100%" stop-color="#5a3c10"/>
    </linearGradient>
  </defs>
  <circle cx="32" cy="32" r="30" fill="url(#cwax)" stroke="#2a0505" stroke-width="1.2"/>
  <circle cx="32" cy="32" r="25" fill="none" stroke="url(#cbrass)" stroke-width="1.2" stroke-dasharray="1.5 2"/>
  <g fill="none" stroke="url(#cbrass)" stroke-width="1.6" stroke-linecap="round">
    <path d="M23 27 C19 30 19 36 24 39"/>
    <path d="M41 27 C45 30 45 36 40 39"/>
    <path d="M27 25 C29 22 35 22 37 25 L37 34 C36 40 28 40 27 34 Z"/>
    <circle cx="30" cy="30" r="1" fill="url(#cbrass)"/>
    <circle cx="34" cy="30" r="1" fill="url(#cbrass)"/>
  </g>
</svg>`;

// Tiny candle-flame SVG used as a header ornament + a flicker on the "typing"
// indicator so the concierge feels like a room lit by a single wick.
const CANDLE_FLAME_SVG = `
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 32' width='14' height='22'>
  <defs>
    <radialGradient id='flame' cx='50%' cy='60%' r='60%'>
      <stop offset='0%' stop-color='#fff2b8'/>
      <stop offset='40%' stop-color='#ffb04a'/>
      <stop offset='80%' stop-color='#c8501e'/>
      <stop offset='100%' stop-color='#3a0800' stop-opacity='0'/>
    </radialGradient>
  </defs>
  <path d='M10 4 C7 10 4 14 4 20 C4 26 7 30 10 30 C13 30 16 26 16 20 C16 14 13 10 10 4Z' fill='url(#flame)'/>
</svg>`;

// Paths we treat as internal and route through React Router. Anything else
// (http://…) opens in a new tab. (Kept inline here for reference — actual
// rendering happens in @/lib/messageRenderer.)

const Concierge = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  // Slugs the backend has confirmed exist. Anything mentioned by the LLM but
  // not in this Set is treated as a hallucination and demoted to plain text.
  const [validSlugs, setValidSlugs] = useState(() => new Set());
  const [invalidSlugs, setInvalidSlugs] = useState(() => new Set());
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/concierge/history/${sessionId.current}`)
      .then((r) => r.json())
      .then((msgs) => {
        if (!Array.isArray(msgs)) return;
        setMessages(
          msgs
            .filter((m) => m && typeof m.role === "string")
            .map((m) => ({ role: m.role, content: m.content || "" })),
        );
      })
      .catch(() => { /* new session — no history yet */ });
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((prev) => [
      ...prev,
      { role: "user", content: text },
      { role: "assistant", content: "" },
    ]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop();
        for (const chunk of chunks) {
          if (!chunk.startsWith("data: ")) continue;
          const evt = JSON.parse(chunk.slice(6));
          if (evt.delta) {
            setMessages((prev) => {
              // Defensive: if the placeholder isn't there (state reset during
              // hot-reload or component remount), append a fresh assistant
              // bubble instead of crashing on undefined.
              if (!prev.length) {
                return [{ role: "assistant", content: evt.delta }];
              }
              const next = [...prev];
              const last = next[next.length - 1];
              if (!last || last.role !== "assistant") {
                next.push({ role: "assistant", content: evt.delta });
              } else {
                next[next.length - 1] = {
                  ...last,
                  content: (last.content || "") + evt.delta,
                };
              }
              return next;
            });
          } else if (evt.error) {
            setMessages((prev) => {
              const next = [...prev];
              if (!next.length) return [{ role: "assistant", content: evt.error }];
              next[next.length - 1] = { role: "assistant", content: evt.error };
              return next;
            });
          }
        }
      }
    } catch {
      setMessages((prev) => {
        const next = [...prev];
        if (!next[next.length - 1]?.content) {
          next[next.length - 1] = {
            role: "assistant",
            content: "The concierge stepped away — please try again.",
          };
        }
        return next;
      });
    } finally {
      setStreaming(false);
      // Validate every product slug the assistant just referenced. Anything
      // not returned by the batch endpoint is either invented or out-of-stock
      // and gets demoted to plain text (link + preview card suppressed).
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && last.content) {
          const slugs = Array.from(new Set(
            [...last.content.matchAll(/\/product\/([a-z0-9-]+)/gi)].map((m) => m[1]),
          ));
          if (slugs.length) {
            fetch(`${API}/products/exists-batch`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ slugs }),
            })
              .then((r) => r.json())
              .then((data) => {
                const good = new Set(data.existing || []);
                setValidSlugs((prevValid) => {
                  const next = new Set(prevValid);
                  good.forEach((s) => next.add(s));
                  return next;
                });
                setInvalidSlugs((prevInvalid) => {
                  const next = new Set(prevInvalid);
                  slugs.forEach((s) => { if (!good.has(s)) next.add(s); });
                  return next;
                });
              })
              .catch(() => {});
          }
        }
        return prev;
      });
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="concierge-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-5 py-4 text-[11px] uppercase tracking-[0.28em] text-bone transition-all duration-500 concierge-fab"
          style={{
            background:
              "linear-gradient(180deg, rgba(120,20,30,0.95) 0%, rgba(65,10,20,0.98) 100%)",
            border: "1px solid rgba(210,150,110,0.4)",
            boxShadow:
              "0 0 30px rgba(120,20,30,0.4), inset 0 0 15px rgba(0,0,0,0.4)",
          }}
          aria-label="Open concierge"
        >
          <MessageCircle strokeWidth={1.5} size={16} />
          Ring the Bell
        </button>
      )}

      {open && (
        <div
          data-testid="concierge-panel"
          className="fixed bottom-6 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[26rem] flex flex-col concierge-panel"
          style={{
            background:
              "linear-gradient(180deg, #14090c 0%, #0a0405 100%)",
            border: "1px solid rgba(180,130,60,0.45)",
            boxShadow:
              "0 30px 80px rgba(0,0,0,0.7), 0 0 40px rgba(120,20,30,0.25), inset 0 0 60px rgba(60,15,20,0.35)",
            height: "min(600px, calc(100vh - 6rem))",
          }}
        >
          {/* Velvet header — deep burgundy gradient, tarnished gold hairline,
              a flickering candle flame as ornament */}
          <div
            className="relative flex items-center justify-between px-5 py-4 overflow-hidden concierge-header"
            style={{
              background:
                "linear-gradient(90deg, rgba(90,15,25,0.95) 0%, rgba(140,30,45,0.85) 50%, rgba(70,10,20,0.95) 100%)",
              borderBottom: "1px solid rgba(210,150,110,0.45)",
            }}
          >
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none opacity-30"
              style={{
                background:
                  "repeating-linear-gradient(180deg, rgba(0,0,0,0) 0px, rgba(0,0,0,0.25) 1px, rgba(0,0,0,0) 2px, rgba(0,0,0,0) 5px)",
              }}
            />
            <div className="relative flex items-center gap-3">
              <div
                className="candle-flicker"
                dangerouslySetInnerHTML={{ __html: CANDLE_FLAME_SVG }}
              />
              <div>
                <div className="font-serif-display text-lg text-bone leading-none">
                  The Concierge
                </div>
                <div
                  className="text-[10px] uppercase tracking-[0.32em] mt-1"
                  style={{ color: "rgba(240,200,150,0.75)" }}
                >
                  Discreet · Always awake
                </div>
              </div>
            </div>
            <button
              data-testid="concierge-close-btn"
              onClick={() => setOpen(false)}
              className="relative text-bone hover:opacity-80"
              aria-label="Close concierge"
            >
              <X strokeWidth={1.5} size={18} />
            </button>
          </div>

          {/* Candlelit chat area */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
            style={{
              background:
                "radial-gradient(ellipse at 50% 0%, rgba(180,90,45,0.09) 0%, rgba(0,0,0,0) 60%)",
            }}
          >
            {messages.length === 0 && (
              <div
                className="text-sm leading-relaxed italic px-4 py-4 border-l-2"
                style={{
                  color: "rgba(240,220,190,0.75)",
                  borderColor: "rgba(180,130,60,0.55)",
                  background: "rgba(20,10,12,0.5)",
                }}
              >
                Good evening. I keep the villa&apos;s ledgers. Ask me what to lay
                out for the occasion — a slow first night, a gift for the wicked,
                a room to disappear in.
              </div>
            )}
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              const isLastAssistant =
                streaming && !isUser && i === messages.length - 1 && !m.content;
              return (
                <div
                  key={i}
                  data-testid={`concierge-msg-${m.role}`}
                  className={`text-sm leading-relaxed max-w-[85%] px-4 py-3 ${
                    isUser ? "ml-auto text-bone" : "text-bone"
                  } concierge-bubble ${isUser ? "user" : "assistant"}`}
                  style={
                    isUser
                      ? {
                          background:
                            "linear-gradient(180deg, rgba(140,30,45,0.95) 0%, rgba(90,15,25,0.95) 100%)",
                          border: "1px solid rgba(210,150,110,0.35)",
                          boxShadow:
                            "0 6px 20px rgba(0,0,0,0.5), inset 0 0 12px rgba(0,0,0,0.35)",
                        }
                      : {
                          background:
                            "linear-gradient(180deg, rgba(30,20,18,0.85) 0%, rgba(18,12,12,0.85) 100%)",
                          border: "1px solid rgba(180,130,60,0.35)",
                          boxShadow: "0 0 22px rgba(180,90,45,0.12)",
                        }
                  }
                >
                  {m.content ? (
                    <>
                      {renderAIMessage(m.content, (url) => {
                        setOpen(false);
                        if (typeof window !== "undefined" && window.location.pathname === url) {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                      }, { invalidSlugs })}
                      {/* Product preview cards — one per validated product
                          mentioned in this bubble. Skipped for hallucinated /
                          out-of-stock slugs so shoppers only see real items. */}
                      {!isUser && extractProductSlugs(m.content)
                        .filter((slug) => validSlugs.has(slug))
                        .map((slug) => (
                          <ConciergeProductCard
                            key={slug}
                            slug={slug}
                            onOpen={(url) => {
                              setOpen(false);
                              if (typeof window !== "undefined" && window.location.pathname === url) {
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }
                            }}
                          />
                        ))}
                    </>
                  ) : (isLastAssistant ? (
                    <span className="inline-flex items-center gap-1.5 opacity-80">
                      <span
                        className="candle-flicker inline-flex"
                        dangerouslySetInnerHTML={{ __html: CANDLE_FLAME_SVG }}
                      />
                      <em className="not-italic" style={{ color: "rgba(240,200,150,0.85)" }}>
                        the concierge listens…
                      </em>
                    </span>
                  ) : "")}
                </div>
              );
            })}
          </div>

          {/* Composer — wax-seal send button */}
          <div
            className="flex items-stretch border-t"
            style={{ borderColor: "rgba(180,130,60,0.35)" }}
          >
            <input
              data-testid="concierge-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Whisper to the concierge…"
              className="flex-1 bg-transparent px-5 py-4 text-sm text-bone outline-none"
              style={{
                fontFamily: "inherit",
              }}
            />
            <button
              data-testid="concierge-send-btn"
              onClick={send}
              disabled={streaming || !input.trim()}
              className="px-4 flex items-center justify-center disabled:opacity-40 transition-all duration-300 wax-send-btn"
              aria-label="Send letter"
              style={{
                background:
                  "linear-gradient(180deg, rgba(90,15,25,0.95) 0%, rgba(50,8,15,0.95) 100%)",
                borderLeft: "1px solid rgba(180,130,60,0.35)",
              }}
              dangerouslySetInnerHTML={{ __html: WAX_SEAL_SVG }}
            />
          </div>
        </div>
      )}

      <style>{`
        /* Slow candle flicker — used in header + assistant "listening" state */
        @keyframes candle-flicker {
          0%, 100% { transform: scale(1)   translateY(0);    opacity: 1;    }
          20%      { transform: scale(0.94) translateY(1px);  opacity: 0.85; }
          45%      { transform: scale(1.05) translateY(-1px); opacity: 1;    }
          70%      { transform: scale(0.97) translateY(0);    opacity: 0.9;  }
        }
        .candle-flicker svg,
        .candle-flicker { animation: candle-flicker 2.6s ease-in-out infinite; }

        /* Wax-seal send button — presses on click, glows on hover */
        .wax-send-btn:not(:disabled):hover  { filter: drop-shadow(0 0 12px rgba(180,50,60,0.75)); }
        .wax-send-btn:not(:disabled):active { transform: scale(0.92); }

        /* Panel fade-in — feels like a curtain being drawn back */
        @keyframes panel-in {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }
        .concierge-panel { animation: panel-in 420ms cubic-bezier(0.22, 1, 0.36, 1); }

        /* Chat bubble entrance */
        @keyframes bubble-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
        .concierge-bubble { animation: bubble-in 320ms ease-out both; }

        /* Floating "ring the bell" button pulse when idle */
        @keyframes fab-pulse {
          0%, 100% { box-shadow: 0 0 30px rgba(120,20,30,0.35), inset 0 0 15px rgba(0,0,0,0.4); }
          50%      { box-shadow: 0 0 45px rgba(180,50,60,0.55), inset 0 0 15px rgba(0,0,0,0.4); }
        }
        .concierge-fab { animation: fab-pulse 3.6s ease-in-out infinite; }
        .concierge-fab:hover { transform: translateY(-1px); }
      `}</style>
    </>
  );
};

export default Concierge;
